import { useState, useRef, useEffect, useCallback } from 'react'
import { conversations, Conversation, Message } from '../lib/api'
import ChatMessage from '../components/ChatMessage'
import ChatInput from '../components/ChatInput'
import TypingIndicator from '../components/TypingIndicator'

// Local message shape expected by ChatMessage component
interface DisplayMessage {
  id: string
  role: 'user' | 'agent'
  content: string
  timestamp: Date
}

function toDisplayMessage(m: Message): DisplayMessage {
  return {
    id: m.id,
    role: m.role === 'user' ? 'user' : 'agent',
    content: m.content,
    timestamp: new Date(m.created_at),
  }
}

export default function ChatPage() {
  const [convId, setConvId] = useState<string | null>(null)
  const [messages, setMessages] = useState<DisplayMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isWaitingForAgent, setIsWaitingForAgent] = useState(false)
  const [initError, setInitError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const convIdRef = useRef<string | null>(null)

  // Keep ref in sync for use in polling callback
  convIdRef.current = convId

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [])

  // Initialize: find or create AGENT_ACTIVITY conversation
  useEffect(() => {
    let cancelled = false

    async function init() {
      try {
        const allConvs = await conversations.list()
        const existing = allConvs.find(
          (c: Conversation) => c.mode === 'AGENT_ACTIVITY'
        )

        if (cancelled) return

        if (existing) {
          setConvId(existing.id)
          // Load existing messages
          const data = await conversations.get(existing.id)
          if (!cancelled) {
            setMessages(data.messages.map(toDisplayMessage))
            // Check if last message is user (daemon may not have responded yet)
            const lastMsg = data.messages[data.messages.length - 1]
            if (lastMsg && lastMsg.role === 'user') {
              setIsWaitingForAgent(true)
              startPolling(existing.id, data.messages.length)
            }
          }
        } else {
          // Create new conversation, then set mode to AGENT_ACTIVITY
          const newConv = await conversations.create({
            idea_id: null,
            title: 'Orchestrator Chat',
          })
          if (cancelled) return
          await conversations.updateMode(newConv.id, 'AGENT_ACTIVITY')
          if (!cancelled) {
            setConvId(newConv.id)
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to initialize chat:', err)
          setInitError('Failed to connect to the server. Please refresh.')
        }
      }
    }

    init()
    return () => { cancelled = true }
  }, [])

  function startPolling(id: string, knownCount: number) {
    if (pollingRef.current) clearInterval(pollingRef.current)

    const knownRef = { count: knownCount }

    pollingRef.current = setInterval(async () => {
      try {
        const data = await conversations.get(id)
        const newMsgs = data.messages

        if (newMsgs.length > knownRef.count) {
          // New message(s) appeared — check if there's an assistant message
          const hasNewAssistant = newMsgs.slice(knownRef.count).some(
            (m: Message) => m.role === 'assistant'
          )
          if (hasNewAssistant) {
            setMessages(newMsgs.map(toDisplayMessage))
            setIsWaitingForAgent(false)
            if (pollingRef.current) {
              clearInterval(pollingRef.current)
              pollingRef.current = null
            }
          }
        }
      } catch (err) {
        console.error('Polling error:', err)
      }
    }, 3000)
  }

  const handleSendMessage = useCallback(async (content: string) => {
    const currentConvId = convIdRef.current
    if (!currentConvId) return

    // Optimistic: add user message to UI immediately
    const tempUserMsg: DisplayMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, tempUserMsg])
    setIsLoading(true)

    try {
      const response = await conversations.sendMessage(currentConvId, content)

      // Replace temp message with real one from server
      if (response.user_message) {
        setMessages(prev => {
          const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id)
          return [...withoutTemp, toDisplayMessage(response.user_message!)]
        })
      }

      if (response.assistant_message) {
        // Synchronous response (shouldn't happen in AGENT_ACTIVITY, but handle it)
        setMessages(prev => [...prev, toDisplayMessage(response.assistant_message!)])
      } else {
        // AGENT_ACTIVITY: daemon will respond asynchronously
        setIsWaitingForAgent(true)
        // Get current message count for polling comparison
        const data = await conversations.get(currentConvId)
        startPolling(currentConvId, data.messages.length)
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      const errorMsg: DisplayMessage = {
        id: `error-${Date.now()}`,
        role: 'agent',
        content: 'Failed to send message. Please try again.',
        timestamp: new Date(),
      }
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id)
        return [...withoutTemp, errorMsg]
      })
    } finally {
      setIsLoading(false)
    }
  }, [])

  if (initError) {
    return (
      <div className="flex-1 flex items-center justify-center px-4">
        <p className="text-red-400">{initError}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Empty state */}
      {messages.length === 0 && !isLoading && (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-2xl text-center space-y-4">
            <h1 className="text-3xl font-bold text-gray-100">
              ReThinker Orchestrator
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              Talk to the local orchestrator. It runs on your daemon with access to
              the claim graph and system state. Messages persist across sessions.
            </p>
            <div className="pt-4 space-y-2 text-sm text-gray-500">
              <p>Responses are generated locally by Ollama — no cloud API cost.</p>
              <p>The daemon checks for your messages every 10 seconds.</p>
            </div>
          </div>
        </div>
      )}

      {/* Message history */}
      {messages.length > 0 && (
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
          {messages.map(message => (
            <ChatMessage key={message.id} message={message} />
          ))}
          {(isLoading || isWaitingForAgent) && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-gray-800 bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <ChatInput
            onSend={handleSendMessage}
            disabled={isLoading || isWaitingForAgent}
          />
        </div>
      </div>
    </div>
  )
}
